import React, { useEffect, useRef } from "react";

// ---------------------------------------------------------------------------
// Shared rendering core for aurora-style patterns.
//
// An aurora pattern paints a deep night-sky background and then layers several
// large, soft, slowly-drifting colored radial gradients on top using the
// "screen" blend mode. The result reads as glowing aurora ribbons that mix
// additively where they overlap, producing the same kind of color shifts the
// previous CSS implementation achieved with `mix-blend-mode: difference` over
// repeating linear gradients — but without any tile/seam artifacts, since
// nothing is tiled.
//
// Variants differ only in which palette they render with:
//   - aurora-lights         → fixed rainbow palette (no user-provided colors)
//   - aurora-colorful-lights → user-provided colors (1..4)
//
// Lives in `internal/` so the auto-discovery in `patterns-js/index.js` (which
// is non-recursive) doesn't try to register it as a pattern.
// ---------------------------------------------------------------------------

const BG_TOP = "#05060f";
const BG_BOTTOM = "#0d0a1f";

// Each ribbon is a soft, oversized radial gradient drifting on its own
// orbit. Six ribbons give enough variety for organic-looking color motion
// without overwhelming the GPU on lower-end devices.
const RIBBON_COUNT = 6;

// Subtle white "shimmer" highlights added on top of the colored ribbons,
// reminiscent of the bright stripes the original CSS pattern carried.
const SHIMMER_COUNT = 3;

function pickColor(colors, idx, fallback) {
  if (!Array.isArray(colors) || colors.length === 0) return fallback;
  return colors[idx % colors.length] || fallback;
}

function hexToRgba(hex, alpha) {
  if (!hex || typeof hex !== "string" || hex[0] !== "#") {
    return `rgba(255,255,255,${alpha})`;
  }
  let value = hex.slice(1);
  if (value.length === 3) {
    value = value
      .split("")
      .map((c) => c + c)
      .join("");
  }
  if (value.length !== 6) return `rgba(255,255,255,${alpha})`;
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function makeRibbons(count) {
  // Stable randomness seeded at component mount keeps each ribbon on its own
  // smooth, non-overlapping trajectory across the whole canvas.
  const ribbons = new Array(count);
  for (let i = 0; i < count; i += 1) {
    ribbons[i] = {
      seed: Math.random() * Math.PI * 2,
      // Each ribbon drifts with its own slow X/Y frequencies so they never
      // sync up and never pulse the entire canvas in lockstep.
      speedX: 0.000018 + Math.random() * 0.000020,
      speedY: 0.000014 + Math.random() * 0.000018,
      ampX: 0.42 + Math.random() * 0.32,
      ampY: 0.30 + Math.random() * 0.25,
      // Vertical anchor spread evenly across the canvas with a touch of jitter.
      baseY: 0.12 + (i / count) * 0.76 + (Math.random() - 0.5) * 0.06,
      radiusFactor: 0.55 + Math.random() * 0.35,
      colorIdx: i,
      intensity: 0.38 + Math.random() * 0.22,
    };
  }
  return ribbons;
}

function makeShimmers(count) {
  const shimmers = new Array(count);
  for (let i = 0; i < count; i += 1) {
    shimmers[i] = {
      seed: Math.random() * Math.PI * 2,
      speedX: 0.000035 + Math.random() * 0.000025,
      speedY: 0.000010 + Math.random() * 0.000015,
      ampX: 0.55 + Math.random() * 0.30,
      ampY: 0.20 + Math.random() * 0.20,
      baseY: 0.20 + (i / Math.max(1, count)) * 0.6,
      radiusFactor: 0.30 + Math.random() * 0.20,
      intensity: 0.18 + Math.random() * 0.10,
    };
  }
  return shimmers;
}

// ---------------------------------------------------------------------------
// Public factory. Returns a React component ready to be exported as the
// default of a pattern file.
//
// Options:
//   - defaultColors:   string[]  — fallback palette when `colors` prop is empty
//   - useUserColors:   boolean   — if false, always render `defaultColors`
//                                  (so aurora-lights doesn't pick up colors
//                                  from neighboring fields by accident)
// ---------------------------------------------------------------------------
export function createAuroraPattern({ defaultColors, useUserColors = true } = {}) {
  const fallback = Array.isArray(defaultColors) && defaultColors.length > 0
    ? defaultColors
    : ["#60a5fa", "#e879f9", "#5eead4", "#a78bfa"];

  return function AuroraPattern({ colors = [], style }) {
    const canvasRef = useRef(null);
    const rafRef = useRef(null);
    const paletteRef = useRef(fallback);

    useEffect(() => {
      if (useUserColors && Array.isArray(colors) && colors.length > 0) {
        paletteRef.current = colors;
      } else {
        paletteRef.current = fallback;
      }
    }, [colors]);

    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return undefined;
      const ctx = canvas.getContext("2d");
      if (!ctx) return undefined;

      let width = 0;
      let height = 0;
      const dpr = typeof window !== "undefined"
        ? Math.min(window.devicePixelRatio || 1, 2)
        : 1;

      const ribbons = makeRibbons(RIBBON_COUNT);
      const shimmers = makeShimmers(SHIMMER_COUNT);

      const resize = () => {
        const parent = canvas.parentElement;
        if (!parent) return;
        // Use clientWidth/Height (layout dims) so an ancestor `transform: scale(...)`
        // (e.g. PreviewDeviceStage) doesn't shrink the canvas and pin it to top-left.
        width = Math.max(1, parent.clientWidth || parent.offsetWidth || 0);
        height = Math.max(1, parent.clientHeight || parent.offsetHeight || 0);
        canvas.width = Math.floor(width * dpr);
        canvas.height = Math.floor(height * dpr);
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      };

      const start = typeof performance !== "undefined"
        ? performance.now()
        : Date.now();

      const drawRibbon = (rib, t, palette) => {
        const cx = width / 2
          + Math.cos(t * rib.speedX + rib.seed) * width * rib.ampX;
        const cy = rib.baseY * height
          + Math.sin(t * rib.speedY + rib.seed * 1.3) * height * rib.ampY;
        const radius = Math.max(width, height) * rib.radiusFactor;
        const color = pickColor(palette, rib.colorIdx, fallback[rib.colorIdx % fallback.length]);

        const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
        grad.addColorStop(0, hexToRgba(color, rib.intensity));
        grad.addColorStop(0.35, hexToRgba(color, rib.intensity * 0.4));
        grad.addColorStop(1, hexToRgba(color, 0));
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, width, height);
      };

      const drawShimmer = (sh, t) => {
        const cx = width / 2
          + Math.cos(t * sh.speedX + sh.seed) * width * sh.ampX;
        const cy = sh.baseY * height
          + Math.sin(t * sh.speedY + sh.seed * 1.7) * height * sh.ampY;
        const radius = Math.max(width, height) * sh.radiusFactor;
        const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
        grad.addColorStop(0, `rgba(255,255,255,${sh.intensity})`);
        grad.addColorStop(0.45, `rgba(255,255,255,${sh.intensity * 0.3})`);
        grad.addColorStop(1, "rgba(255,255,255,0)");
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, width, height);
      };

      const tick = (rawNow) => {
        const now = rawNow
          || (typeof performance !== "undefined" ? performance.now() : Date.now());
        const t = now - start;
        const palette = paletteRef.current;

        ctx.globalCompositeOperation = "source-over";
        const bgGrad = ctx.createLinearGradient(0, 0, 0, height);
        bgGrad.addColorStop(0, BG_TOP);
        bgGrad.addColorStop(1, BG_BOTTOM);
        ctx.fillStyle = bgGrad;
        ctx.fillRect(0, 0, width, height);

        ctx.globalCompositeOperation = "screen";
        for (let i = 0; i < ribbons.length; i += 1) {
          drawRibbon(ribbons[i], t, palette);
        }
        for (let i = 0; i < shimmers.length; i += 1) {
          drawShimmer(shimmers[i], t);
        }

        ctx.globalCompositeOperation = "source-over";
        rafRef.current = requestAnimationFrame(tick);
      };

      resize();
      rafRef.current = requestAnimationFrame(tick);

      let ro = null;
      if (typeof ResizeObserver !== "undefined" && canvas.parentElement) {
        ro = new ResizeObserver(resize);
        ro.observe(canvas.parentElement);
      } else if (typeof window !== "undefined") {
        window.addEventListener("resize", resize);
      }

      return () => {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        if (ro) {
          try { ro.disconnect(); } catch { /* noop */ }
        } else if (typeof window !== "undefined") {
          window.removeEventListener("resize", resize);
        }
      };
    }, []);

    return (
      <canvas
        ref={canvasRef}
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          display: "block",
          pointerEvents: "none",
          ...style,
        }}
      />
    );
  };
}
